import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import image2 from '../../assets/image_2.png';
import image3 from '../../assets/image_3.png';
import { ShareIcon, DevicePhoneMobileIcon, SparklesIcon, BuildingOfficeIcon, TrophyIcon, SwatchIcon, UserCircleIcon, PencilSquareIcon, CloudIcon } from '@heroicons/react/24/outline';
import profilePublic from '../../assets/profile-public.png';
import profileEdit from '../../assets/profile-edit.png';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

// Animation variants
const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

const PhoneFrame = ({ children }: { children: React.ReactNode }) => (
  <div className="relative w-[320px] lg:w-[430px] h-[694px] lg:h-[932px] bg-black rounded-[55px] p-[12px] shadow-xl">
    {/* Dynamic Island */}
    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[126px] h-[37px] bg-black rounded-b-[18px] z-20" />
    
    {/* Screen Content */}
    <div className="relative w-full h-full bg-white rounded-[43px] overflow-hidden z-10">
      {children}
    </div>
  </div>
);

export default function WhyThis() {
  const { t } = useTranslation();
  const [showEdit, setShowEdit] = useState(false);
  const [hasScrollTriggered, setHasScrollTriggered] = useState(false);
  const imageRef = useRef(null);
  const isInView = useInView(imageRef, { amount: 0.8 });

  // Effect to handle the one-time scroll trigger
  useEffect(() => {
    if (isInView && !hasScrollTriggered) {
      setTimeout(() => {
        setShowEdit(true);
        setHasScrollTriggered(true);
      }, 2000);
    }
  }, [isInView, hasScrollTriggered]);

  return (
    <div className="relative">
      {/* Stats Section */}
      <section className="bg-primary pt-8 pb-32 relative overflow-hidden">
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]" 
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '24px 24px',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
          <motion.div 
            className="relative"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
          >
            <p className="text-white/80 text-xl md:text-2xl text-center mb-6">
              {t('howItWorks.stats.intro')}
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8 md:gap-6"
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Stat 1 */}
            <motion.div 
              className="text-center space-y-3 relative"
              variants={itemVariants}
            >
              <div className="text-5xl md:text-6xl font-bold text-secondary">
                {t(`howItWorks.stats.stat1.value`)}
              </div>
              <div className="text-white/90 text-lg md:text-xl leading-relaxed">
                {t(`howItWorks.stats.stat1.text`)}
              </div>
            </motion.div>

            {/* Stat 2 */}
            <motion.div 
              className="text-center space-y-3 relative"
              variants={itemVariants}
            >
              <div className="text-5xl md:text-6xl font-bold text-secondary">
                {t(`howItWorks.stats.stat2.value`)}
              </div>
              <div className="text-white/90 text-lg md:text-xl leading-relaxed">
                {t(`howItWorks.stats.stat2.text`)}
              </div>
            </motion.div>

            {/* Stat 3 */}
            <motion.div 
              className="text-center space-y-3 relative"
              variants={itemVariants}
            >
              <div className="text-5xl md:text-6xl font-bold text-secondary">
                {t(`howItWorks.stats.stat3.value`)}
              </div>
              <div className="text-white/90 text-lg md:text-xl leading-relaxed">
                {t(`howItWorks.stats.stat3.text`)}
              </div>
            </motion.div>
          </motion.div>

          {/* New Impact Section */}
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 mt-24">
            <motion.div 
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 md:p-12"
              variants={sectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.p 
                className="text-white/90 text-xl md:text-2xl text-center mb-12"
                variants={itemVariants}
              >
                {t('howItWorks.impact.intro')}
              </motion.p>

              <motion.div 
                className="grid md:grid-cols-3 gap-8"
                variants={itemVariants}
              >
                <div className="relative px-6 py-4 border-l-2 border-secondary/30">
                  <div className="text-4xl font-bold text-secondary mb-3">
                    {t(`howItWorks.impact.stat1.value`)}
                  </div>
                  <p className="text-white/80 text-lg leading-relaxed">
                    {t(`howItWorks.impact.stat1.text`)}
                  </p>
                </div>

                <div className="relative px-6 py-4 border-l-2 border-secondary/30">
                  <div className="text-4xl font-bold text-secondary mb-3">
                    {t(`howItWorks.impact.stat2.value`)}
                  </div>
                  <p className="text-white/80 text-lg leading-relaxed">
                    {t(`howItWorks.impact.stat2.text`)}
                  </p>
                </div>

                <div className="relative px-6 py-4 border-l-2 border-secondary/30">
                  <div className="text-4xl font-bold text-secondary mb-3">
                    {t(`howItWorks.impact.stat3.value`)}
                  </div>
                  <p className="text-white/80 text-lg leading-relaxed">
                    {t(`howItWorks.impact.stat3.text`)}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* New Overlapping Transition Card */}
      <div className="absolute left-0 right-0 -mt-16 z-10">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <motion.div 
            className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl p-8 text-center
              border border-secondary/10"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p className="text-2xl md:text-3xl text-primary font-medium">
              {t('howItWorks.transition')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* New Explanation Section - SVGs with connecting arrows - matching Professional Profiles background */}
      <section className="pt-40 pb-24 bg-white/50 overflow-hidden">
        <div className="max-w-full mx-auto px-6">
          <motion.div 
            className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-0"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* SVG 1 */}
            <motion.div 
              className="relative flex justify-center items-center w-full md:w-auto px-2 mt-10 md:mt-0"
              variants={itemVariants}
            >
              <img 
                src="/svg/explanation-1.svg" 
                alt="Tap to Share" 
                className="w-full md:w-[600px] h-auto"
              />
            </motion.div>

            {/* Arrow 1 */}
            <motion.div 
              className="flex md:hidden items-center justify-center z-10"
              variants={itemVariants}
              transition={{ delay: 0.1 }}
            >
              <svg width="40" height="60" viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.7071 59.7071C20.3166 60.0976 19.6834 60.0976 19.2929 59.7071L12.9289 53.3431C12.5384 52.9526 12.5384 52.3195 12.9289 51.9289C13.3195 51.5384 13.9526 51.5384 14.3431 51.9289L20 57.5858L25.6569 51.9289C26.0474 51.5384 26.6805 51.5384 27.0711 51.9289C27.4616 52.3195 27.4616 52.9526 27.0711 53.3431L20.7071 59.7071ZM21 0V59H19V0L21 0Z" fill="#1A2330" stroke="#1A2330" strokeWidth="3"/>
              </svg>
            </motion.div>

            {/* Desktop Arrow 1 (unchanged) */}
            <motion.div 
              className="hidden md:flex items-center justify-center z-10 mx-2"
              variants={itemVariants}
              transition={{ delay: 0.1 }}
            >
              <svg width="80" height="70" viewBox="0 0 80 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M79.7071 20.7071C80.0976 20.3166 80.0976 19.6834 79.7071 19.2929L73.3431 12.9289C72.9526 12.5384 72.3195 12.5384 71.9289 12.9289C71.5384 13.3195 71.5384 13.9526 71.9289 14.3431L77.5858 20L71.9289 25.6569C71.5384 26.0474 71.5384 26.6805 71.9289 27.0711C72.3195 27.4616 72.9526 27.4616 73.3431 27.0711L79.7071 20.7071ZM0 21H79V19H0V21Z" fill="#1A2330" stroke="#1A2330" strokeWidth="3"/>
              </svg>
            </motion.div>

            {/* SVG 2 */}
            <motion.div 
              className="relative flex justify-center items-center w-full md:w-auto px-2"
              variants={itemVariants}
              transition={{ delay: 0.2 }}
            >
              <img 
                src="/svg/explanation-2.svg" 
                alt="View Profile" 
                className="w-full md:w-[600px] h-auto"
              />
            </motion.div>

            {/* Arrow 2 */}
            <motion.div 
              className="flex md:hidden items-center justify-center z-10"
              variants={itemVariants}
              transition={{ delay: 0.3 }}
            >
              <svg width="40" height="60" viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.7071 59.7071C20.3166 60.0976 19.6834 60.0976 19.2929 59.7071L12.9289 53.3431C12.5384 52.9526 12.5384 52.3195 12.9289 51.9289C13.3195 51.5384 13.9526 51.5384 14.3431 51.9289L20 57.5858L25.6569 51.9289C26.0474 51.5384 26.6805 51.5384 27.0711 51.9289C27.4616 52.3195 27.4616 52.9526 27.0711 53.3431L20.7071 59.7071ZM21 0V59H19V0L21 0Z" fill="#1A2330" stroke="#1A2330" strokeWidth="3"/>
              </svg>
            </motion.div>

            {/* Desktop Arrow 2 (unchanged) */}
            <motion.div 
              className="hidden md:flex items-center justify-center z-10 mx-2"
              variants={itemVariants}
              transition={{ delay: 0.3 }}
            >
              <svg width="80" height="70" viewBox="0 0 80 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M79.7071 20.7071C80.0976 20.3166 80.0976 19.6834 79.7071 19.2929L73.3431 12.9289C72.9526 12.5384 72.3195 12.5384 71.9289 12.9289C71.5384 13.3195 71.5384 13.9526 71.9289 14.3431L77.5858 20L71.9289 25.6569C71.5384 26.0474 71.5384 26.6805 71.9289 27.0711C72.3195 27.4616 72.9526 27.4616 73.3431 27.0711L79.7071 20.7071ZM0 21H79V19H0V21Z" fill="#1A2330" stroke="#1A2330" strokeWidth="3"/>
              </svg>
            </motion.div>

            {/* SVG 3 */}
            <motion.div 
              className="relative flex justify-center items-center w-full md:w-auto px-2"
              variants={itemVariants}
              transition={{ delay: 0.4 }}
            >
              <img 
                src="/svg/explanation-3.svg" 
                alt="Connect" 
                className="w-full md:w-[600px] h-auto"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Product Showcase */}
      <section id="features" className="bg-background relative">
        {/* Section 1: Instant Connections */}
        <div className="flex items-start">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-16">
            <motion.div 
              className="grid lg:grid-cols-2 gap-16 items-center"
              variants={sectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
            >
              {/* Desktop Image - hidden on mobile */}
              <motion.div 
                className="hidden lg:block relative"
                variants={itemVariants}
              >
                <div className="aspect-[4/3] bg-white rounded-2xl shadow-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-secondary/10" />
                  <div className="relative p-8 flex items-center justify-center h-full">
                    <img 
                      src={image2} 
                      alt="Three Colors Shot" 
                      className="w-full h-full object-contain rounded-lg transform scale-110"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Content */}
              <motion.div 
                className="space-y-8"
                variants={itemVariants}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-primary">
                  {t('howItWorks.features.instant.title')}
                </h2>

                {/* Mobile Image */}
                <motion.div 
                  className="block lg:hidden relative"
                  variants={itemVariants}
                >
                  <div className="aspect-[4/3] bg-white rounded-2xl shadow-2xl overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-secondary/10" />
                    <div className="relative p-8 flex items-center justify-center h-full">
                      <img 
                        src={image2} 
                        alt="Three Colors Shot" 
                        className="w-full h-full object-contain rounded-lg transform scale-110"
                      />
                    </div>
                  </div>
                </motion.div>

                <p className="text-xl text-text/80 leading-relaxed">
                  {t('howItWorks.features.instant.description')}
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <ShareIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.instant.benefits.sharing.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.instant.benefits.sharing.text')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <DevicePhoneMobileIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.instant.benefits.compatibility.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.instant.benefits.compatibility.text')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <SparklesIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.instant.benefits.integration.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.instant.benefits.integration.text')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Section 2: Professional Profiles */}
        <div className="min-h-screen flex items-center bg-white/50">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-24">
            <motion.div 
              className="grid lg:grid-cols-2 gap-16 items-center"
              variants={sectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {/* Content */}
              <motion.div 
                className="space-y-8"
                variants={itemVariants}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-primary">
                  {t('howItWorks.features.profiles.title')}
                </h2>

                {/* Mobile Image */}
                <div 
                  className="block lg:hidden relative w-[320px] lg:w-[430px] mx-auto cursor-pointer" 
                  ref={imageRef}
                  onClick={() => setShowEdit(current => !current)}
                >
                  <PhoneFrame>
                    <div className="relative w-full h-full">
                      <img 
                        src={profilePublic} 
                        alt="Public Profile View" 
                        className="w-full h-full object-cover absolute inset-0"
                      />
                      <img 
                        src={profileEdit} 
                        alt="Edit Profile View" 
                        className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-700
                          ${showEdit ? 'opacity-100' : 'opacity-0'}`}
                      />
                    </div>
                  </PhoneFrame>
                </div>

                <p className="text-xl text-text/80 leading-relaxed">
                  {t('howItWorks.features.profiles.description')}
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <UserCircleIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.profiles.benefits.autopopulated.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.profiles.benefits.autopopulated.text')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <PencilSquareIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.profiles.benefits.customizable.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.profiles.benefits.customizable.text')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <CloudIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.profiles.benefits.privacy.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.profiles.benefits.privacy.text')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Desktop Image - hidden on mobile */}
              <div className="hidden lg:block relative">
                <div className="flex items-center justify-center">
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="relative"
                  >
                    <PhoneFrame>
                      <div className="relative w-full h-full">
                        <img 
                          src={profilePublic} 
                          alt="Public Profile View" 
                          className="w-full h-full object-cover absolute inset-0"
                        />
                        <img 
                          src={profileEdit} 
                          alt="Edit Profile View" 
                          className="w-full h-full object-cover absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300"
                        />
                      </div>
                    </PhoneFrame>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Section 3: Branded for Your Event */}
        <div className="min-h-screen flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-24">
            <motion.div 
              className="grid lg:grid-cols-2 gap-16 items-center"
              variants={sectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {/* Content */}
              <motion.div 
                className="space-y-8"
                variants={itemVariants}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-primary">
                  {t('howItWorks.features.whiteLabel.title')}
                </h2>

                {/* Mobile Image */}
                <motion.div 
                  className="block lg:hidden relative"
                  variants={itemVariants}
                >
                  <div className="aspect-[4/3] bg-white rounded-2xl shadow-2xl overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-secondary/10" />
                    <div className="relative p-8 flex items-center justify-center h-full">
                      <img 
                        src={image3} 
                        alt="Branded Comparison Shot" 
                        className="w-full h-full object-contain rounded-lg transform scale-110"
                      />
                    </div>
                  </div>
                </motion.div>

                <p className="text-xl text-text/80 leading-relaxed">
                  {t('howItWorks.features.whiteLabel.description')}
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <BuildingOfficeIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.whiteLabel.benefits.branding.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.whiteLabel.benefits.branding.text')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <TrophyIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.whiteLabel.benefits.materials.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.whiteLabel.benefits.materials.text')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                      <SwatchIcon className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary">
                        {t('howItWorks.features.whiteLabel.benefits.colors.title')}
                      </h3>
                      <p className="text-text/70">
                        {t('howItWorks.features.whiteLabel.benefits.colors.text')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Desktop Image - hidden on mobile */}
              <motion.div 
                className="hidden lg:block relative"
                variants={itemVariants}
              >
                <div className="aspect-[4/3] bg-white rounded-2xl shadow-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-secondary/10" />
                  <div className="relative p-8 flex items-center justify-center h-full">
                    <img 
                      src={image3} 
                      alt="Branded Comparison Shot" 
                      className="w-full h-full object-contain rounded-lg transform scale-110"
                    />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
